import { useState, useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

export default function usePrescriptionInterventions(initialFilters = {}) {
  const [interventions, setInterventions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  })
  const [filters, setFilters] = useState({
    sort: 'startTime,desc',
    ...initialFilters,
  })

  const buildApiParams = useCallback(
    (pageOverride = null) => {
      const params = {
        page: pageOverride !== null ? pageOverride : pagination.page,
        size: pagination.size,
        ...filters,
      }

      // Handle date filters - convert to ISO-8601 format if needed
      if (params.from) {
        if (params.from instanceof Date) {
          params.from = params.from.toISOString()
        }
      }
      if (params.to) {
        if (params.to instanceof Date) {
          params.to = params.to.toISOString()
        }
      }

      // Handle array parameters - convert to comma-separated strings if needed
      const arrayParams = [
        'prescribedAssessments',
        'prescribedActions',
        'therapiesNeeded',
        'prescribedTherapy',
        'psychAssessmentIds',
        'psychAssessmentTitles',
      ]

      arrayParams.forEach((param) => {
        if (Array.isArray(params[param])) {
          params[param] = params[param].join(',')
        }
      })

      // Remove empty/null/undefined filters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === '' ||
          params[key] === null ||
          params[key] === undefined ||
          (Array.isArray(params[key]) && params[key].length === 0)
        ) {
          delete params[key]
        }
      })

      return params
    },
    [filters, pagination.page, pagination.size],
  )

  const fetchInterventions = useCallback(
    async (pageOverride = null) => {
      setLoading(true)
      setError(null)

      try {
        const params = buildApiParams(pageOverride)
        const response = await getSPDataWithParams(
          '/api/secure/cc/support/prescriptions/interventions',
          params,
        )

        if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const data = getDataMapFromCommonResponse(response)?.['prescription-interventions']
          setInterventions(data?.content || [])
          setPagination((prev) => ({
            ...prev,
            page: data?.pageable?.pageNumber || 0,
            totalElements: data?.totalElements || 0,
            totalPages: data?.totalPages || 0,
          }))
        } else {
          const errorMessage =
            response?.data?.message || response?.data?.description || 'Failed to fetch interventions'
          setError(errorMessage)
          toast.error(errorMessage)
        }
      } catch (err) {
        console.error('Error fetching interventions:', err)
        const errorMessage = err?.response?.data?.message || 'An error occurred while fetching interventions'
        setError(errorMessage)
        toast.error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [buildApiParams],
  )

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }))
  }, [])

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }))
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }))
  }, [])

  const clearFilter = useCallback((key) => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters[key]
      return newFilters
    })
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }))
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters({
      sort: 'startTime,desc',
    })
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }))
  }, [])

  const changePage = useCallback(
    (newPage) => {
      setPagination((prev) => ({
        ...prev,
        page: newPage,
      }))
    },
    [],
  )

  const changePageSize = useCallback((newSize) => {
    setPagination((prev) => ({
      ...prev,
      size: newSize,
      page: 0,
    }))
  }, [])

  // Auto-fetch when filters or pagination changes
  useEffect(() => {
    fetchInterventions()
  }, [filters, pagination.page, pagination.size])

  return {
    interventions,
    loading,
    error,
    pagination,
    filters,
    fetchInterventions,
    updateFilter,
    updateFilters,
    clearFilter,
    clearAllFilters,
    changePage,
    changePageSize,
  }
}

